import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { Code2, Layers, Server, Smartphone, Globe, Database, ArrowRight, CheckCircle } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'

const B = 'https://pixner.net/bankio/main/assets/images'

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

const services = [
  { icon: <Globe size={22} />, title: 'Web Development', desc: 'Custom websites, e-commerce stores, and web apps built for performance and conversion.' },
  { icon: <Smartphone size={22} />, title: 'Mobile Apps', desc: 'Native and cross-platform iOS & Android apps with clean UX and seamless backend integration.' },
  { icon: <Server size={22} />, title: 'Backend & APIs', desc: 'Scalable REST APIs, microservices, and cloud infrastructure on AWS, GCP, or Azure.' },
  { icon: <Database size={22} />, title: 'Database Design', desc: 'Optimized relational and NoSQL database architecture for high-throughput systems.' },
  { icon: <Layers size={22} />, title: 'System Integration', desc: 'Connect your ERP, CRM, payment gateways, and third-party tools into one seamless workflow.' },
  { icon: <Code2 size={22} />, title: 'Payment Integration', desc: 'Embed A1XPAY or any payment gateway into your platform with our guided API integration support.' },
]

/* Hero visual — decorative code card */
function HeroVisual() {
  return (
    <motion.div className="sv-hero-visual"
      initial={{ opacity: 0, x: 40, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
      <div className="sv-hv-card">
        <div className="sv-hv-dots"><span /><span /><span /></div>
        <div className="sv-hv-code">
          <span className="sv-hv-kw">const</span> <span className="sv-hv-fn">payment</span> = <span className="sv-hv-kw">await</span> <span className="sv-hv-fn">a1xpay</span>
          <span className="sv-hv-dim">.</span><span className="sv-hv-fn">create</span>(<span className="sv-hv-dim">{'{'}</span><br />
          &nbsp;&nbsp;<span className="sv-hv-key">amount</span><span className="sv-hv-dim">:</span> <span className="sv-hv-num">5000</span><span className="sv-hv-dim">,</span><br />
          &nbsp;&nbsp;<span className="sv-hv-key">currency</span><span className="sv-hv-dim">:</span> <span className="sv-hv-str">"INR"</span><span className="sv-hv-dim">,</span><br />
          &nbsp;&nbsp;<span className="sv-hv-key">method</span><span className="sv-hv-dim">:</span> <span className="sv-hv-str">"upi"</span><br />
          <span className="sv-hv-dim">{'}'}</span>)<span className="sv-hv-dim">;</span>
        </div>
        <div className="sv-hv-badge sv-hv-success"><CheckCircle size={13} /> Payment Successful · ₹5,000</div>
        <div className="sv-hv-stats">
          <div className="sv-hv-stat"><div className="sv-hv-stat-num">99.9%</div><div className="sv-hv-stat-lbl">Uptime</div></div>
          <div className="sv-hv-stat"><div className="sv-hv-stat-num">&lt;200ms</div><div className="sv-hv-stat-lbl">Response</div></div>
          <div className="sv-hv-stat"><div className="sv-hv-stat-num">REST</div><div className="sv-hv-stat-lbl">API</div></div>
        </div>
      </div>
    </motion.div>
  )
}

export default function SoftwareDevelopment() {
  return (
    <>
      <style>{css}</style>
      <div className="sv-root">
        <Navbar />

        <section className="sv-hero">
          <div className="sv-container sv-hero-grid">
            <motion.div className="sv-hero-text"
              initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <span className="sv-sub">Services</span>
              <h1 className="sv-hero-h1">Software<br />Development</h1>
              <p className="sv-hero-p">Custom technology solutions built for real businesses — web, mobile, APIs, and integrations delivered on time.</p>
              <div className="sv-hero-btns">
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Link to="/contact-us" className="sv-btn-primary">Get a free quote <ArrowRight size={16} /></Link>
                </motion.div>
              </div>
              <div className="sv-trust-row">
                {['Web & Mobile', 'API Dev', 'Cloud Ready'].map(t => (
                  <span key={t} className="sv-trust-tag"><span className="sv-trust-dot" />{t}</span>
                ))}
              </div>
            </motion.div>
            <HeroVisual />
          </div>
        </section>

        <section className="sv-section sv-bg-white">
          <div className="sv-container sv-feat-grid">
            <FadeUp className="sv-feat-text">
              <div className="sv-circle"><Code2 size={26} color="#fff" /></div>
              <span className="sv-sub sv-green">What we build</span>
              <h2 className="sv-h2">Technology that works as hard as you do</h2>
              <p className="sv-para">From your first website to a full enterprise platform, our engineering team delivers clean, maintainable, and scalable code. We don't just build software — we understand your business goals and build to achieve them.</p>
              <ul className="sv-list">
                {['Agile development with weekly sprint demos', 'Full source code ownership transferred to you', 'Post-launch support and maintenance available', '3-year technology stack relevance guarantee'].map(t => (
                  <li key={t} className="sv-li"><div className="sv-dot" /><span>{t}</span></li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.1} className="sv-img">
              <motion.img src={`${B}/feature-item-1.png`} alt="Software development"
                whileHover={{ scale: 1.04 }} transition={{ type: 'spring', stiffness: 200 }} />
            </FadeUp>
          </div>
        </section>

        <section className="sv-section sv-bg-light">
          <div className="sv-container">
            <FadeUp><div className="sv-sec-head"><span className="sv-sub sv-green">Our capabilities</span><h2 className="sv-h2">Everything you need, under one roof</h2></div></FadeUp>
            <div className="sv-cards">
              {services.map((s, i) => (
                <FadeUp key={s.title} delay={i * 0.07}>
                  <motion.div className="sv-card" whileHover={{ y: -4, borderColor: '#B0BAF1', boxShadow: '0 12px 32px rgba(26,77,190,0.12)' }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                    <div className="sv-card-icon">{s.icon}</div>
                    <h4 className="sv-card-title">{s.title}</h4>
                    <p className="sv-card-desc">{s.desc}</p>
                  </motion.div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Second feature section */}
        <section className="sv-section sv-bg-light">
          <div className="sv-container sv-feat-grid sv-feat-rev">
            <FadeUp delay={0.1} className="sv-img">
              <motion.img src={`${B}/card-left-Illus.png`} alt="Development process"
                whileHover={{ scale: 1.04 }} transition={{ type: 'spring', stiffness: 200 }} />
            </FadeUp>
            <FadeUp className="sv-feat-text">
              <div className="sv-circle"><Layers size={26} color="#fff" /></div>
              <span className="sv-sub sv-green">Our process</span>
              <h2 className="sv-h2">From idea to live product — fast</h2>
              <p className="sv-para">We follow a structured yet flexible delivery model that keeps you in control at every stage. No black boxes, no surprises — just clean code shipped on schedule.</p>
              <ul className="sv-list">
                {['Discovery & scope finalization in week 1', 'Weekly sprint demos with live test builds', 'Thorough QA across devices and browsers', 'Smooth handover with full documentation'].map(t => (
                  <li key={t} className="sv-li"><div className="sv-dot" /><span>{t}</span></li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </section>

        {/* Illustrated CTA */}
        <section className="sv-cta">
          <div className="sv-container sv-cta-flex">
            <FadeUp className="sv-cta-text">
              <span className="sv-sub sv-white">Let's build something great</span>
              <h2 className="sv-cta-h2">Ready to start your project?</h2>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Link to="/contact-us" className="sv-btn-white">Talk to our team <ArrowRight size={16} /></Link>
              </motion.div>
            </FadeUp>
            <motion.div className="sv-cta-illu"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>
              <img src={`${B}/get-start.png`} alt="Get started" />
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');
.sv-root{font-family:'Jost',sans-serif;background:#fff;overflow-x:hidden;}
.sv-container{max-width:1200px;margin:0 auto;padding:0 1.5rem;}
@media(min-width:1200px){.sv-container{padding:0 2.5rem;}}

/* Hero 2-column */
.sv-hero{background:#f5faff;padding:90px 0 70px;position:relative;overflow:hidden;}
.sv-hero::before{content:'';position:absolute;width:500px;height:500px;border-radius:50%;background:rgba(26,77,190,0.07);top:-150px;right:-100px;pointer-events:none;}
.sv-hero-grid{display:grid;grid-template-columns:1fr;gap:3rem;align-items:center;}
@media(min-width:1024px){.sv-hero-grid{grid-template-columns:1fr 1fr;}}
.sv-hero-text{max-width:580px;}
.sv-sub{font-size:1rem;font-weight:600;color:#055C2D;margin-bottom:0.875rem;display:block;}
.sv-green{color:#055C2D;}
.sv-white{color:rgba(255,255,255,0.85);}
.sv-hero-h1{font-size:clamp(2rem,4.5vw,3.5rem);font-weight:800;color:#0A1023;letter-spacing:-0.04em;line-height:1.08;margin-bottom:1.25rem;}
.sv-hero-p{font-size:1.0625rem;color:#2C3655;line-height:1.75;max-width:480px;margin-bottom:1.75rem;}
.sv-hero-btns{display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:1.5rem;}
.sv-trust-row{display:flex;gap:0.75rem;flex-wrap:wrap;}
.sv-trust-tag{display:inline-flex;align-items:center;gap:0.35rem;font-size:0.8125rem;font-weight:500;color:#2C3655;background:#fff;border:1px solid #E8EBF4;border-radius:50px;padding:0.3rem 0.75rem;}
.sv-trust-dot{width:7px;height:7px;border-radius:50%;background:#1A4DBE;flex-shrink:0;}

/* Hero visual card */
.sv-hero-visual{display:none;}
@media(min-width:1024px){.sv-hero-visual{display:block;}}
.sv-hv-card{background:#fff;border:1.5px solid #E8EBF4;border-radius:1.25rem;padding:1.75rem;box-shadow:0 12px 40px rgba(26,77,190,0.12);font-family:'Jost',sans-serif;}
.sv-hv-dots{display:flex;gap:0.4rem;margin-bottom:1.25rem;}
.sv-hv-dots span{width:10px;height:10px;border-radius:50%;}
.sv-hv-dots span:nth-child(1){background:#FF5F56;}
.sv-hv-dots span:nth-child(2){background:#FFBD2E;}
.sv-hv-dots span:nth-child(3){background:#27C93F;}
.sv-hv-code{background:#0A1023;border-radius:0.75rem;padding:1.25rem 1.5rem;font-family:monospace;font-size:0.8125rem;line-height:1.9;margin-bottom:1rem;color:#94A3B8;}
.sv-hv-kw{color:#C792EA;}
.sv-hv-fn{color:#82AAFF;}
.sv-hv-key{color:#F07178;}
.sv-hv-str{color:#C3E88D;}
.sv-hv-num{color:#F78C6C;}
.sv-hv-dim{color:#546E7A;}
.sv-hv-badge{display:inline-flex;align-items:center;gap:0.4rem;font-size:0.8125rem;font-weight:600;padding:0.4rem 0.875rem;border-radius:50px;margin-bottom:1rem;}
.sv-hv-success{background:#DCFCE7;color:#15803D;}
.sv-hv-stats{display:flex;gap:1rem;}
.sv-hv-stat{flex:1;text-align:center;background:#f5faff;border-radius:0.625rem;padding:0.625rem 0.5rem;}
.sv-hv-stat-num{font-size:0.9375rem;font-weight:800;color:#1A4DBE;letter-spacing:-0.02em;}
.sv-hv-stat-lbl{font-size:0.7rem;color:#64748B;font-weight:500;margin-top:0.15rem;}

.sv-section{padding:80px 0;}
.sv-bg-white{background:#fff;}
.sv-bg-light{background:#f5faff;}
.sv-feat-grid{display:grid;grid-template-columns:1fr;gap:3rem;align-items:center;}
@media(min-width:1024px){.sv-feat-grid{grid-template-columns:1fr 1fr;gap:5rem;}}
.sv-img{display:flex;align-items:center;justify-content:center;}
.sv-img img{max-width:100%;max-height:400px;object-fit:contain;}
.sv-circle{width:58px;height:58px;border-radius:50%;background:linear-gradient(135deg,#1A4DBE 0%,#06B6D4 100%);margin-bottom:1.5rem;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 20px rgba(26,77,190,0.28);}
.sv-h2{font-size:clamp(1.75rem,3.5vw,2.5rem);font-weight:700;color:#0A1023;letter-spacing:-0.03em;line-height:1.2;margin-bottom:1rem;}
.sv-para{font-size:1rem;color:#2C3655;line-height:1.75;margin-bottom:1.5rem;}
.sv-list{list-style:none;padding:0;margin:0 0 1.75rem;display:flex;flex-direction:column;gap:0.75rem;}
.sv-li{display:flex;align-items:center;gap:0.625rem;}
.sv-dot{min-width:22px;min-height:22px;border-radius:50%;background:#055C2D;display:flex;align-items:center;justify-content:center;}
.sv-dot::after{content:'';width:8px;height:5px;border-left:2px solid #fff;border-bottom:2px solid #fff;transform:rotate(-45deg);display:block;margin-top:-2px;}
.sv-li span{font-size:1rem;color:#2C3655;font-weight:500;}
.sv-sec-head{text-align:center;margin-bottom:3rem;}
.sv-cards{display:grid;grid-template-columns:1fr;gap:1.25rem;}
@media(min-width:640px){.sv-cards{grid-template-columns:repeat(2,1fr);}}
@media(min-width:1024px){.sv-cards{grid-template-columns:repeat(3,1fr);}}
.sv-card{background:#fff;border:1.5px solid #E8EBF4;border-radius:1rem;padding:1.5rem;transition:border-color 0.2s,transform 0.2s,box-shadow 0.2s;cursor:default;}
.sv-card-icon{width:2.5rem;height:2.5rem;background:#EEF3FF;border-radius:0.5rem;display:flex;align-items:center;justify-content:center;color:#1A4DBE;margin-bottom:0.875rem;}
.sv-card-title{font-size:1rem;font-weight:700;color:#0A1023;margin-bottom:0.375rem;}
.sv-card-desc{font-size:0.875rem;color:#2C3655;line-height:1.6;}
.sv-feat-rev .sv-img{order:0;}
@media(min-width:1024px){.sv-feat-rev .sv-img{order:-1;}}
.sv-feat-text{display:flex;flex-direction:column;}
.sv-cta{background:linear-gradient(135deg,#1A4DBE 0%,#1540a8 100%);padding:80px 0;position:relative;overflow:hidden;}
.sv-cta::before{content:'';position:absolute;width:400px;height:400px;border-radius:50%;background:rgba(255,255,255,0.05);top:-150px;right:-80px;}
.sv-cta-flex{position:relative;z-index:2;display:flex;align-items:center;justify-content:space-between;gap:2rem;flex-wrap:wrap;}
.sv-cta-text{flex:1;min-width:280px;text-align:left;}
.sv-cta-illu{display:none;}
@media(min-width:900px){.sv-cta-illu{display:block;width:200px;flex-shrink:0;}}
.sv-cta-illu img{max-width:100%;filter:brightness(1.1) drop-shadow(0 8px 24px rgba(0,0,0,0.2));}
.sv-cta-h2{font-size:clamp(1.875rem,4vw,2.75rem);font-weight:800;color:#fff;letter-spacing:-0.04em;margin-bottom:2rem;}
.sv-btn-primary{display:inline-flex;align-items:center;gap:0.4rem;padding:0.7rem 1.75rem;font-size:1rem;font-weight:600;color:#fff;background:#1A4DBE;border:1.5px solid #1A4DBE;border-radius:50px;text-decoration:none;transition:all 0.2s;font-family:'Jost',sans-serif;box-shadow:0 4px 16px rgba(26,77,190,0.3);}
.sv-btn-primary:hover{background:#1540a8;box-shadow:0 8px 24px rgba(26,77,190,0.4);}
.sv-btn-white{display:inline-flex;align-items:center;gap:0.4rem;padding:0.7rem 1.75rem;font-size:1rem;font-weight:600;color:#1A4DBE;background:#fff;border:1.5px solid #fff;border-radius:50px;text-decoration:none;transition:all 0.2s;font-family:'Jost',sans-serif;box-shadow:0 4px 16px rgba(0,0,0,0.1);}
.sv-btn-white:hover{background:#f0f4ff;}
`
